import { useState } from 'react'

import useOrg from 'components/Orgs/org-context'
import { ManageTrial } from 'components/Workspaces/ManageTrial'
import { sva } from 'styled-system/css'
import { Box } from 'styled-system/jsx'
import { Text } from 'ui-kit/text'

const trialIndicatorRecipe = sva({
  slots: ['root', 'text', 'span'],
  base: {
    root: {
      padding: 2,
      textAlign: 'center',
      cursor: 'pointer',
    },
    text: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexWrap: 'wrap',
      rowGap: 0.5,
      columnGap: 1,
      margin: 0,
      fontWeight: 'semibold',
      color: 'white',
    },
    span: {
      backgroundColor: 'white/20',
      paddingInline: 1,
      paddingBlock: 0.5,
      borderRadius: 'l2',
      fontWeight: 'medium',
    },
  },
  variants: {
    variant: {
      trial: {
        root: {
          backgroundColor: 'primary.a11',
          _hover: {
            backgroundColor: 'primary.a10',
          },
        },
      },
      warning: {
        root: {
          backgroundColor: 'warning.a9',
          _hover: {
            backgroundColor: 'warning.a8',
          },
        },
      },
      expired: {
        root: {
          backgroundColor: 'error.a11',
          _hover: {
            backgroundColor: 'error.11/70',
          },
        },
      },
    },
  },
  defaultVariants: {
    variant: 'trial',
  },
})

export function MainMenuTrialIndicator() {
  const { org } = useOrg()

  const [manageTrialOpen, setManageTrialOpen] = useState(false)

  const trialEndDate = org?.trialEndDate
  const showTrialBlock = !!trialEndDate

  if (!showTrialBlock) {
    return null
  }

  const daysOfTrial = getTrialDays(new Date(trialEndDate))

  const trialAlmostExpired = daysOfTrial > 0 && daysOfTrial <= 5
  const trialExpired = daysOfTrial <= 0
  const [amount, interval] = getReadableTrialTime(daysOfTrial)

  const variant = trialExpired
    ? 'expired'
    : trialAlmostExpired
      ? 'warning'
      : 'trial'

  const classnames = trialIndicatorRecipe({ variant })

  return (
    <>
      <Box className={classnames.root}>
        <Text
          className={classnames.text}
          onClick={() => setManageTrialOpen(true)}
        >
          Trial{' '}
          <span className={classnames.span}>
            {amount}&thinsp;{interval}
          </span>
        </Text>
      </Box>
      {manageTrialOpen && (
        <ManageTrial onClose={() => setManageTrialOpen(false)} />
      )}
    </>
  )
}

export function getTrialDays(date: Date) {
  const deltaDays = Math.ceil(
    ((date.getTime() - Date.now()) * 1.0) / 1000 / 60 / 60 / 24,
  )
  return Math.round(deltaDays)
}

function getReadableTrialTime(days: number): [amount: number, period: string] {
  const years = Math.floor(days / 365)
  const months = Math.floor(days / 30)

  if (years >= 1) return [years, 'y']
  if (months > 3) return [months, 'm']

  return [days, 'd']
}
